/*
  Shopify Online Store 2.0 theme script
  Leveraging Modern Custom Elements (Web Components)
*/

// Dynamic money formatting helper
class CurrencyFormatter {
  static format(cents, format = '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return format.replace(placeholderRegex, value);
  }
}

// 1. Variant Selectors Component
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  connectedCallback() {
    this.updateOptions();
    this.updateMasterId();
    if (this.currentVariant) {
      this.updatePrice(this.currentVariant.price, this.currentVariant.compare_at_price);
    }
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.updatePrice('', '');
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updatePrice(this.currentVariant.price, this.currentVariant.compare_at_price);
      this.toggleAddButton(!this.currentVariant.available, 'Sold Out', false);
    }
  }

  updateOptions() {
    const checkedInputs = Array.from(this.querySelectorAll('.variant-picker__input:checked, input[type="radio"]:checked'));
    checkedInputs.sort((a, b) => {
      const posA = parseInt(a.dataset.optionPosition || '0', 10);
      const posB = parseInt(b.dataset.optionPosition || '0', 10);
      return posA - posB;
    });
    this.options = checkedInputs.map((input) => input.value);
  }

  getVariantOptions(variant) {
    if (variant.options && variant.options.length) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  updateMasterId() {
    const variants = this.getVariantData();
    this.currentVariant = variants.find((variant) => {
      const variantOptions = this.getVariantOptions(variant);
      return variantOptions.every((option, index) => this.options[index] === option);
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;

    const mediaContainer = document.querySelector('[data-product-media]');
    if (!mediaContainer) return;

    const mediaId = this.currentVariant.featured_media.id;
    const gallery = mediaContainer.closest('[id^="gallery-"]');
    const thumb = gallery?.querySelector(`.thumb-btn[data-media-id="${mediaId}"]`);

    const mainImg =
      mediaContainer.querySelector('.product-gallery__main-image') ||
      mediaContainer.querySelector('[id^="main-image-"]');

    if (thumb && mainImg) {
      mainImg.src = thumb.dataset.src || this.currentVariant.featured_media.preview_image.src;
      mainImg.alt = thumb.dataset.alt || this.currentVariant.featured_media.alt || '';
    } else if (mainImg && this.currentVariant.featured_media.preview_image) {
      mainImg.src = this.currentVariant.featured_media.preview_image.src;
      mainImg.alt = this.currentVariant.featured_media.alt || '';
    }

    const carousel = mediaContainer.querySelector('.product-gallery__carousel');
    const slide = carousel?.querySelector(`[data-media-id="${mediaId}"]`);
    if (carousel && slide) {
      carousel.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  getProductScope() {
    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      const section = document.querySelector(`[data-section="${sectionId}"]`);
      if (section) return section;
    }
    return this.closest('[data-section]') || document;
  }

  updateVariantInput() {
    if (!this.currentVariant) return;

    const scope = this.getProductScope();
    const inputs = scope.querySelectorAll('input[name="id"], [data-variant-id]');
    inputs.forEach((input) => {
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePrice(price, compareAtPrice) {
    const scope = this.getProductScope();
    const priceCurrent = scope.querySelector('[data-product-price-current]');
    const priceCompare = scope.querySelector('[data-product-price-compare]');

    if (!priceCurrent) {
      const priceContainer = scope.querySelector('[data-product-price]');
      if (!priceContainer) return;
      if (!price) {
        priceContainer.textContent = '';
        return;
      }
      const formattedPrice = CurrencyFormatter.format(price, window.theme.moneyFormat);
      if (compareAtPrice && compareAtPrice > price) {
        const formattedCompare = CurrencyFormatter.format(compareAtPrice, window.theme.moneyFormat);
        priceContainer.innerHTML = `<span class="price-item price-item--sale">${formattedPrice}</span> <s class="price-item price-item--regular">${formattedCompare}</s>`;
      } else {
        priceContainer.innerHTML = `<span class="price-item price-item--regular">${formattedPrice}</span>`;
      }
      return;
    }

    if (!price) {
      priceCurrent.textContent = '';
      if (priceCompare) priceCompare.classList.add('hidden');
      return;
    }

    priceCurrent.textContent = CurrencyFormatter.format(price, window.theme.moneyFormat);

    if (priceCompare) {
      if (compareAtPrice && compareAtPrice > price) {
        priceCompare.textContent = CurrencyFormatter.format(compareAtPrice, window.theme.moneyFormat);
        priceCompare.classList.remove('hidden');
      } else {
        priceCompare.textContent = '';
        priceCompare.classList.add('hidden');
      }
    }
  }

  toggleAddButton(disable, text, modifyClass = false) {
    const scope = this.getProductScope();
    const addButton = scope.querySelector('product-form button[type="submit"][name="add"], form[action*="/cart/add"] button[type="submit"]');
    if (!addButton) return;

    const labelSpan = addButton.querySelector('span.text-base.font-bold');
    const stickyAdd = document.querySelector('[data-sticky-add]');

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text && labelSpan) labelSpan.textContent = text;
      if (stickyAdd) {
        stickyAdd.setAttribute('disabled', 'disabled');
        if (text) stickyAdd.textContent = text;
      }
    } else {
      addButton.removeAttribute('disabled');
      if (labelSpan) labelSpan.textContent = 'Add to Basket';
      if (stickyAdd) {
        stickyAdd.removeAttribute('disabled');
        stickyAdd.textContent = 'Add to Basket';
      }
    }
  }

  getVariantData() {
    if (!this.variantData) {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return [];
      this.variantData = JSON.parse(script.textContent);
    }
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

// 2. AJAX Product Form Component (Add to Cart without page reload)
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.initQuantityStepper();
  }

  initQuantityStepper() {
    const input = this.querySelector('.product-quantity__input');
    if (!input) return;

    const decreaseBtn = this.querySelector('[data-qty-decrease]');
    const increaseBtn = this.querySelector('[data-qty-increase]');
    const min = parseInt(input.getAttribute('min'), 10) || 1;
    const max = parseInt(input.getAttribute('max'), 10) || 99;

    const sync = () => {
      let value = parseInt(input.value, 10);
      if (Number.isNaN(value) || value < min) value = min;
      if (value > max) value = max;
      input.value = value;
      if (decreaseBtn) decreaseBtn.disabled = value <= min;
      if (increaseBtn) increaseBtn.disabled = value >= max;
    };

    decreaseBtn?.addEventListener('click', () => {
      input.value = Math.max(min, parseInt(input.value, 10) - 1);
      sync();
    });

    increaseBtn?.addEventListener('click', () => {
      input.value = Math.min(max, parseInt(input.value, 10) + 1);
      sync();
    });

    input.addEventListener('change', sync);
    input.addEventListener('blur', sync);
    sync();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    submitButton.setAttribute('disabled', 'disabled');
    submitButton.classList.add('loading');

    const formData = new FormData(this.form);
    formData.append('sections', 'cart-drawer');

    const config = {
      method: 'POST',
      headers: {
        'Accept': 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };

    fetch(`${window.theme.routes.cartAddUrl}.js`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:added', {
          detail: { item: response }
        }));
        
        // Trigger a custom event to update cart icons/drawers
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { 
            cart: response,
            sections: response.sections
          }
        }));
        
        // Open the cart drawer
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('loading');
      });
  }

  handleErrorMessage(errorMessage) {
    alert(errorMessage);
  }
}

customElements.define('product-form', ProductForm);

// 3. Cart Drawer Component
class CartDrawer {
  constructor() {
    this.drawer = document.querySelector('#CartDrawer');
    if (!this.drawer) return;

    this.itemsContainer = this.drawer.querySelector('[data-cart-drawer-items]');
    this.totalContainer = this.drawer.querySelector('[data-cart-drawer-total]');
    this.descriptionContainer = this.drawer.querySelector('[data-cart-drawer-description]');
    this.footerContainer = this.drawer.querySelector('[data-cart-drawer-footer]');
    
    this.toggleButtons = document.querySelectorAll('[data-cart-drawer-toggle]');
    this.closeButtons = this.drawer.querySelectorAll('[data-cart-drawer-close]');

    this.init();
  }

  init() {
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Event delegation for cart actions
    this.drawer.addEventListener('click', (e) => {
      const updateBtn = e.target.closest('[data-cart-update]');
      const removeBtn = e.target.closest('[data-cart-remove]');

      if (updateBtn) {
        this.updateQuantity(updateBtn.dataset.cartUpdate, updateBtn.dataset.qty);
      } else if (removeBtn) {
        this.updateQuantity(removeBtn.dataset.cartRemove, 0);
      }
    });

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:updated', (event) => {
      if (event.detail.sections && event.detail.sections['cart-drawer']) {
        this.renderContents(event.detail.sections['cart-drawer']);
      } else {
        this.refreshDrawer();
      }
      this.updateCartCounts(event.detail.cart.item_count || event.detail.cart.total_count);
    });
  }

  open() {
    this.drawer.classList.add('active');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.drawer.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }

  async updateQuantity(key, quantity) {
    this.itemsContainer.classList.add('opacity-50', 'pointer-events-none');
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          id: key, 
          quantity: parseInt(quantity),
          sections: 'cart-drawer'
        })
      });
      const data = await response.json();
      
      if (data.sections && data.sections['cart-drawer']) {
        this.renderContents(data.sections['cart-drawer']);
      }
      this.updateCartCounts(data.item_count);
    } catch (e) {
      console.error('Error updating cart:', e);
    } finally {
      this.itemsContainer.classList.remove('opacity-50', 'pointer-events-none');
    }
  }

  async refreshDrawer() {
    try {
      const response = await fetch(`${window.location.origin}/cart?sections=cart-drawer`);
      const data = await response.json();
      if (data['cart-drawer']) this.renderContents(data['cart-drawer']);
    } catch (e) {
      console.error('Error refreshing cart drawer:', e);
    }
  }

  renderContents(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newItems = doc.querySelector('[data-cart-drawer-items]');
    const newTotal = doc.querySelector('[data-cart-drawer-total]');
    const newDesc = doc.querySelector('[data-cart-drawer-description]');
    const newFooter = doc.querySelector('[data-cart-drawer-footer]');

    if (newItems && this.itemsContainer) this.itemsContainer.innerHTML = newItems.innerHTML;
    if (newTotal && this.totalContainer) this.totalContainer.innerHTML = newTotal.innerHTML;
    if (newDesc && this.descriptionContainer) this.descriptionContainer.innerHTML = newDesc.innerHTML;
    
    if (this.footerContainer && newFooter) {
      this.footerContainer.style.display = newFooter.style.display;
    }
  }

  updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

// 4. Collection Filters Component (AJAX Filtering)
class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.container = document.querySelector('#ProductGridContainer');

    this.addEventListener('change', this.onFormChange.bind(this));
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('#CollectionFiltersForm');
      if (form) event.preventDefault();
    });

    if (!CollectionFilters.paginationBound) {
      CollectionFilters.paginationBound = true;
      document.addEventListener('click', (event) => {
        const link = event.target.closest('[data-pagination-link]');
        if (!link) return;
        if (!document.querySelector('#ProductGridContainer')?.contains(link)) return;
        event.preventDefault();
        const filters = document.querySelector('collection-filters');
        if (filters) filters.updateView(link.href);
      });
    }
  }

  getForm() {
    return this.querySelector('#CollectionFiltersForm');
  }

  buildFilterUrl(form) {
    const searchParams = new URLSearchParams();

    for (const [key, value] of new FormData(form).entries()) {
      if (value === '' || value == null) continue;
      searchParams.append(key, value);
    }

    const query = searchParams.toString();
    return query ? `${form.action}?${query}` : form.action;
  }

  onFormChange(event) {
    const form = event.target.closest('#CollectionFiltersForm');
    if (!form || !this.contains(form)) return;

    if (event.target.matches('input[type="number"]')) {
      clearTimeout(this._priceDebounce);
      this._priceDebounce = setTimeout(() => {
        this.updateView(this.buildFilterUrl(form));
      }, 450);
      return;
    }

    this.updateView(this.buildFilterUrl(form));
  }

  async updateView(url) {
    const section = document.querySelector('#CollectionSection');
    if (!section) return;

    const sectionId = section.dataset.id;
    const overlay = this.container?.querySelector('.loading-overlay');
    if (overlay) overlay.classList.remove('hidden');

    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`Collection filter request failed (${response.status})`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      const newFiltersRoot = doc.querySelector('#CollectionFiltersRoot');
      const filtersRoot = document.querySelector('#CollectionFiltersRoot');
      const newGrid = doc.querySelector('#ProductGridContainer');
      const newCount = doc.querySelector('[data-product-count]');

      if (newFiltersRoot && filtersRoot) {
        filtersRoot.innerHTML = newFiltersRoot.innerHTML;
      }

      if (newGrid && this.container) {
        this.container.innerHTML = newGrid.innerHTML;
        window.dispatchEvent(new CustomEvent('ashlyn:wishlist:updated'));
      }

      if (newCount) {
        const countEl = document.querySelector('[data-product-count]');
        if (countEl) countEl.textContent = newCount.textContent;
      }

      window.history.pushState({ path: fetchUrl.pathname + fetchUrl.search }, '', fetchUrl.pathname + fetchUrl.search);
    } catch (error) {
      console.error('Error filtering collection:', error);
      window.location.href = url;
    } finally {
      if (overlay) overlay.classList.add('hidden');
      window.scrollTo({ top: section.offsetTop - 100, behavior: 'smooth' });
    }
  }
}

customElements.define('collection-filters', CollectionFilters);

// 5. Hero Slider Component
class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('[data-slider-viewport]');
    this.container = this.querySelector('[data-slider-container]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.currentIndex = 0;
    this.autoplayInterval = 6000;
    this.touchStartX = 0;
    this.touchDeltaX = 0;
    this.gap = 12;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prevSlide();
        this.startAutoplay();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.nextSlide();
        this.startAutoplay();
      });
    }

    if (this.dots.length > 0) {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goToSlide(index);
          this.startAutoplay();
        });
      });
    }

    this.initTouchSwipe();
    this.onResize = () => this.updateSlider();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('load', () => this.updateSlider());
    this.updateSlider();
    this.startAutoplay();

    this.querySelectorAll('.hero-slider__image').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => {
        this.updateSlider();
        this.syncMobileViewportHeight();
      }, { once: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.stopAutoplay();
  }

  isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  initTouchSwipe() {
    if (!this.viewport || this.slides.length < 2) return;

    this.viewport.addEventListener(
      'touchstart',
      (event) => {
        this.touchStartX = event.changedTouches[0].screenX;
        this.touchDeltaX = 0;
        this.stopAutoplay();
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchmove',
      (event) => {
        this.touchDeltaX = event.changedTouches[0].screenX - this.touchStartX;
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchend',
      () => {
        if (Math.abs(this.touchDeltaX) > 40) {
          if (this.touchDeltaX < 0) {
            this.nextSlide();
          } else {
            this.prevSlide();
          }
        }
        this.startAutoplay();
      },
      { passive: true }
    );
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlider();
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.updateSlider();
  }

  prevSlide() {
    this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.updateSlider();
  }

  updateSlider() {
    if (!this.container || !this.viewport || this.slides.length === 0) return;

    const viewportWidth = this.viewport.getBoundingClientRect().width;
    const gap = this.isDesktop() ? 20 : 12;
    const slideWidth = this.isDesktop() ? viewportWidth * 0.82 : viewportWidth;

    this.viewport.style.setProperty('--hero-slide-width', `${slideWidth}px`);

    this.slides.forEach((item) => {
      item.style.flexBasis = `${slideWidth}px`;
      item.style.width = `${slideWidth}px`;
      item.style.maxWidth = `${slideWidth}px`;
    });

    let offset;
    if (this.isDesktop()) {
      const centered = viewportWidth / 2 - slideWidth / 2 - this.currentIndex * (slideWidth + gap);
      offset = this.currentIndex === 0 ? 0 : centered;
    } else {
      offset = -this.currentIndex * (slideWidth + gap);
    }

    this.container.style.transform = `translateX(${offset}px)`;
    this.container.style.gap = `${gap}px`;

    this.slides.forEach((item, index) => {
      item.classList.toggle('is-active', index === this.currentIndex);
    });

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
    });

    this.syncMobileViewportHeight();
  }

  syncMobileViewportHeight() {
    if (!this.viewport || this.slides.length === 0) return;

    if (this.isDesktop()) {
      this.viewport.style.removeProperty('height');
      this.viewport.style.removeProperty('min-height');
      return;
    }

    const activeSlide = this.slides[this.currentIndex];
    if (!activeSlide) return;

    window.requestAnimationFrame(() => {
      this.viewport.style.height = `${activeSlide.offsetHeight}px`;
      this.viewport.style.minHeight = '';
    });
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.slides.length < 2) return;
    this.autoplay = setInterval(() => this.nextSlide(), this.autoplayInterval);
  }

  stopAutoplay() {
    clearInterval(this.autoplay);
  }
}

customElements.define('hero-slider', HeroSlider);

// 4. Header Interactions
document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('[data-header-wrapper]');
  if (!header) return;

  const searchToggles = header.querySelectorAll('[data-search-toggle]');
  const searchContainer = header.querySelector('[data-search-container]');
  const mobileMenuToggle = header.querySelector('[data-mobile-menu-toggle]');

  if (searchToggles.length && searchContainer) {
    const setSearchOpen = (isOpen) => {
      searchContainer.classList.toggle('header-search-bar--hidden', !isOpen);
      searchToggles.forEach((btn) => {
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
      if (isOpen) {
        const input = searchContainer.querySelector('.site-search-form__input');
        if (input) input.focus();
      }
    };

    searchToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const isOpen = searchContainer.classList.contains('header-search-bar--hidden');
        setSearchOpen(isOpen);
      });
    });
  }

  const mobileMenu = document.querySelector('[data-mobile-menu]');
  if (mobileMenuToggle && mobileMenu) {
    const closeMenu = () => {
      mobileMenu.classList.add('hidden');
      mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    };

    mobileMenuToggle.addEventListener('click', () => {
      mobileMenu.classList.remove('hidden');
      mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
    });

    mobileMenu.querySelectorAll('[data-close-menu]').forEach((el) => {
      el.addEventListener('click', closeMenu);
    });
  }

  // Initialize Cart Drawer
  if (document.querySelector('#CartDrawer')) {
    new CartDrawer();
  }
});
